import { useCallback, useEffect, useState } from 'react';
import {
  ApiKey,
  BACKEND_URL,
  CreatedApiKey,
  MCP_URL,
  createApiKey,
  listApiKeys,
  revokeApiKey,
} from './developerApi';
import { currentMarket } from './market';
import { fmtDateTime, zoneForCountry } from './timeFormat';
import './Developer.css';

type SnippetTab = 'claude' | 'codex' | 'rest';

const KEY_PLACEHOLDER = 'YOUR_API_KEY';

function snippetFor(tab: SnippetTab, key: string): string {
  switch (tab) {
    case 'claude':
      return `claude mcp add --transport http callout ${MCP_URL} \\
  --header "Authorization: Bearer ${key}"`;
    case 'codex':
      return `# ~/.codex/config.toml
[mcp_servers.callout]
url = "${MCP_URL}"
bearer_token_env_var = "CALLOUT_API_KEY"

# then, in your shell:
export CALLOUT_API_KEY="${key}"`;
    case 'rest':
      return `curl -X POST ${BACKEND_URL}/dialout \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to_number": "${currentMarket().defaultCountry === 'US' ? '+14155550142' : '+819012345678'}",
    "language": "${currentMarket().defaultLanguage}",
    "prompt": "Book a table for 2 this Saturday at 7pm under the name Sato."
  }'`;
  }
}

/** In Japan time on callout.jp, in the viewer's own zone on heycallout.com. */
function formatDate(value: string | null): string {
  if (!value) return 'Never';
  return fmtDateTime(value, zoneForCountry(currentMarket().defaultCountry));
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button className="dev-btn dev-btn-secondary" onClick={copy}>
      {copied ? 'Copied' : label}
    </button>
  );
}

function Developer() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [snippetTab, setSnippetTab] = useState<SnippetTab>('claude');

  const refresh = useCallback(async () => {
    try {
      setKeys(await listApiKeys());
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const created = await createApiKey(newKeyName.trim());
      setCreatedKey(created);
      setNewKeyName('');
      setError('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key.');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key: ApiKey) => {
    if (!window.confirm(`Revoke "${key.name}"? Anything using this key will stop working.`)) return;
    try {
      await revokeApiKey(key.id);
      if (createdKey?.id === key.id) setCreatedKey(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke API key.');
    }
  };

  const snippet = snippetFor(snippetTab, createdKey?.key ?? KEY_PLACEHOLDER);

  return (
    <div className="dev-container">
      <header className="dev-header">
        <h1>Developer</h1>
        <p className="dev-muted">
          Place calls from your own code or AI agents. Calls made with an API key use your
          account's credits, just like calls made here.
        </p>
      </header>

      {error && <div className="dev-error">{error}</div>}

      <section className="dev-section">
        <h2>API keys</h2>

        {createdKey && (
          <div className="dev-new-key" role="status">
            <div className="dev-new-key-title">
              Your new key "{createdKey.name}". Copy it now: it won't be shown again.
            </div>
            <div className="dev-key-row">
              <code className="dev-key">{createdKey.key}</code>
              <CopyButton text={createdKey.key} />
            </div>
            <button className="dev-link" onClick={() => setCreatedKey(null)}>
              I've saved it
            </button>
          </div>
        )}

        <form className="dev-create" onSubmit={handleCreate}>
          <input
            className="dev-input"
            type="text"
            placeholder="Key name, e.g. Claude Code on my laptop"
            value={newKeyName}
            maxLength={64}
            onChange={(e) => setNewKeyName(e.target.value)}
            aria-label="New API key name"
          />
          <button className="dev-btn dev-btn-primary" type="submit" disabled={creating || !newKeyName.trim()}>
            {creating ? 'Creating…' : 'Create key'}
          </button>
        </form>

        {loading ? (
          <div className="dev-muted">Loading…</div>
        ) : keys.length === 0 ? (
          <div className="dev-empty">You don't have any API keys yet.</div>
        ) : (
          <ul className="dev-key-list">
            {keys.map((key) => (
              <li key={key.id} className="dev-key-item">
                <div className="dev-key-info">
                  <div className="dev-key-name">{key.name}</div>
                  <code className="dev-key-preview">{key.preview}</code>
                  <div className="dev-key-meta">
                    Created {formatDate(key.createdAt)} · Last used {formatDate(key.lastUsedAt)}
                  </div>
                </div>
                <button className="dev-btn dev-btn-danger" onClick={() => handleRevoke(key)}>
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="dev-section">
        <h2>Connect</h2>
        <p className="dev-muted">
          Use Callout as an MCP server in Claude Code, Codex and other MCP clients, or call the
          REST API directly.
          {!createdKey && ` Replace ${KEY_PLACEHOLDER} with one of your keys.`}
        </p>
        <p className="dev-muted">
          A call lasts up to 4 minutes for one credit. For complex matters pass{' '}
          <code>max_duration_seconds: 480</code> to allow 8 minutes; that holds a second
          credit, charged only if the call runs longer than 4 minutes.
        </p>

        <div className="dev-snippet-bar">
          <div className="dev-tabs" role="tablist">
            {(
              [
                ['claude', 'Claude Code'],
                ['codex', 'Codex'],
                ['rest', 'REST API'],
              ] as [SnippetTab, string][]
            ).map(([tab, label]) => (
              <button
                key={tab}
                role="tab"
                aria-selected={snippetTab === tab}
                className={`dev-tab ${snippetTab === tab ? 'active' : ''}`}
                onClick={() => setSnippetTab(tab)}
              >
                {label}
              </button>
            ))}
          </div>
          <CopyButton text={snippet} />
        </div>
        <div className="dev-snippet">
          <pre>{snippet}</pre>
        </div>

        <dl className="dev-endpoints">
          <dt>MCP endpoint</dt>
          <dd><code>{MCP_URL}</code></dd>
          <dt>REST endpoint</dt>
          <dd><code>POST {BACKEND_URL}/dialout</code></dd>
        </dl>
      </section>
    </div>
  );
}

export default Developer;
